/**
 * PATH: packages/email-hub/src/feature/lib/attachmentMention.ts
 * FILE_ID: EMAILHUB-LIB-ATTACHMENTION-001
 * PURPOSE: Pure I→O detector for "I said I attached something but didn't"
 *          (FEAT-2026-07-06-COMPOSER-MICRO §attachment guard).
 * WHY:     The classic operator fuck-up email clients solved a decade ago —
 *          "see attached" + Send with zero attachments. Detection scans ONLY
 *          the operator's own words: text above the quote boundary, with the
 *          opaque signature block removed, so a quoted "see attached" from
 *          the ORIGINAL message (or a signature) never trips the guard.
 * FLOW:
 *   (subject, bodyHtml) ──ownWordsFromCompose──▶ typed text
 *                       ──mentionsAttachment──▶ boolean (gate the send)
 * RELATED:
 *   - emailHtml.ts typingAreaIsBlank: same quote-boundary heuristic —
 *     keep the marker regex in sync.
 *   - ComposePage triggerSend: consults this before arming the send hold.
 */

/** Same quote-boundary signals as emailHtml.typingAreaIsBlank — the
 *  markers that survive the TipTap round-trip plus the legacy ones that
 *  cover stale drafts. */
const QUOTE_BOUNDARY_RE =
  /border-left:3px solid #cbd5e1|-+\s*Forwarded message|data-quote=|<hr\b/i

/** Word patterns that read as "there is a file with this email".
 *  Deliberately conservative: bare "file"/"document" alone is far too
 *  noisy ("please file this", "the document register"), so every branch
 *  requires an attach/enclose verb or a canonical phrase. */
const MENTION_RE = new RegExp(
  [
    '\\battach(?:ed|ment|ments|ing)?\\b',
    '\\benclos(?:ed|ing|ure)\\b',
    '\\bplease find (?:the )?(?:attached|enclosed)\\b',
    '\\bsee (?:the )?(?:attached|enclosed)\\b',
    '\\bpfa\\b',
  ].join('|'),
  'i',
)

/** Extract the operator's OWN words from the compose state: subject +
 *  body text above the quote boundary, signature block excluded.
 *  Pure: (subject, bodyHtml) → plain text. */
export function ownWordsFromCompose(subject: string, bodyHtml: string): string {
  let html = bodyHtml || ''
  const markerIdx = html.search(QUOTE_BOUNDARY_RE)
  if (markerIdx >= 0) {
    const lt = html.lastIndexOf('<', markerIdx)
    html = html.slice(0, lt >= 0 ? lt : markerIdx)
  }
  // The signature travels as ONE opaque element (signatureBlock.ts); its
  // encoded payload would otherwise leak words like "attached" from a
  // pasted signature banner into the scan.
  html = html.replace(/<div[^>]*data-qte-sig=[^>]*>[\s\S]*?<\/div>/gi, ' ')
  const text = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
  return `${subject || ''} ${text}`.trim()
}

/** Does the message CLAIM an attachment? Pure: text → boolean. */
export function mentionsAttachment(ownWords: string): boolean {
  return MENTION_RE.test(ownWords)
}

/** The full gate: should the "no attachment?" confirm interrupt this send?
 *  Pure: (subject, bodyHtml, attachmentCount) → boolean. */
export function shouldWarnMissingAttachment(
  subject: string,
  bodyHtml: string,
  attachmentCount: number,
): boolean {
  if (attachmentCount > 0) return false
  return mentionsAttachment(ownWordsFromCompose(subject, bodyHtml))
}
